import { Session } from 'src/contexts/SessionContext';
import { PlatformOnboarding } from 'src/models/platform-onboarding/_types';
import { TaskCompletionStatus } from 'src/models/platform-onboarding/_types';
import { User } from 'src/models/user/_types';
import { isDreamcoatActive } from 'src/shared/dreamcoat';

interface OnboardingStep {
  id: string;
  label: string;
  isComplete: boolean;
  parentId?: string;
}

export interface OnboardingProgress {
  steps: OnboardingStep[];
  completedSteps: number;
  totalSteps: number;
  progressPercentage: number;
}

// Helper function to check if a CSM task is complete (true or 'not_utilizing')
const isCsmTaskComplete = (status: TaskCompletionStatus): boolean => {
  return status === true || status === 'not_utilizing';
};

export const calculateOnboardingProgress = (
  platform: Session,
  onboarding: PlatformOnboarding,
  trainer: User
): OnboardingProgress => {
  const isLocationPlatform = platform.platformType === 'location';

  const dreamcoatFrame = isDreamcoatActive(platform, trainer);

  const steps: OnboardingStep[] = [
    {
      id: 'welcome',
      label: 'Welcome',
      isComplete: !!onboarding.welcomeCompleted,
    },
    {
      id: 'basics',
      label: 'Basic Information',
      isComplete: !!(
        platform.businessProfile?.supportAddress?.line1 &&
        platform.businessProfile?.supportAddress?.city &&
        platform.businessProfile?.supportAddress?.state &&
        platform.businessProfile?.supportAddress?.postalCode
      ),
    },
    {
      id: 'branding',
      label: 'Branding',
      isComplete: false, // Will be calculated based on sub-items
    },
    ...(!dreamcoatFrame
      ? [
          {
            id: 'branding_colors',
            label: 'Colors',
            parentId: 'branding',
            isComplete: !!(
              platform.theme?.primary || onboarding.colorTheme?.primary
            ),
          },
        ]
      : []),
    {
      id: 'branding_logos',
      label: 'Logos & Images',
      parentId: 'branding',
      isComplete: !!(
        // Check for logos
        (
          (onboarding?.logoImage || platform.logoUrl) &&
          // Check for app images only for non-location platforms
          (isLocationPlatform ||
            (onboarding?.appIcon &&
              onboarding?.splashImage &&
              onboarding?.shareImage))
        )
      ),
    },
    {
      id: 'technical',
      label: 'Technical Setup',
      isComplete: false, // Will be calculated based on sub-items
    },
    {
      id: 'technical_stripe',
      label: 'Connect Stripe',
      parentId: 'technical',
      isComplete: !!(
        // Check if the user has opted out of domain setup
        (
          onboarding?.skipStripeSetup === true ||
          // Or check if the domain is verified
          trainer.stripeConnected
        )
      ),
    },
    {
      id: 'technical_domain',
      label: 'Domain',
      parentId: 'technical',
      isComplete: !!(
        // Check if the user has opted out of domain setup
        (
          onboarding?.skipDomainSetup === true ||
          // Or check if the domain is verified
          (trainer.domainVerification?.dns?.verification &&
            trainer.domainVerification?.dns?.domain &&
            trainer.domainVerification?.mapping)
        )
      ),
    },
    // Only include App Store step for non-location platforms
    ...(isLocationPlatform
      ? []
      : [
          {
            id: 'technical_app_store',
            label: 'App Store',
            parentId: 'technical',
            isComplete: !!(
              // Check for app information
              (
                onboarding?.optOutOfApps === true ||
                (platform?.appShortName &&
                  platform?.appLongName &&
                  platform?.appDescription &&
                  // AND EITHER (Self-managed AND tasks done) OR (Exercise-managed AND form done)
                  ((onboarding?.notManagedApps === true &&
                    onboarding?.appStoreData?.dunsNumber &&
                    onboarding?.appStoreData?.googlePaymentProfileId &&
                    isCsmTaskComplete(onboarding.csmSetup?.appleInviteSent) &&
                    isCsmTaskComplete(
                      onboarding.csmSetup?.googleCredentialsEmailed
                    )) ||
                    // If notManagedApps is false (exercise-managed), check if form data is filled out
                    (onboarding?.notManagedApps === false &&
                      onboarding?.appStoreData &&
                      // Check for essential form fields for exercise-managed
                      onboarding?.appStoreData?.legalEntityName &&
                      onboarding?.appStoreData?.countryRegion &&
                      onboarding?.appStoreData?.streetAddress &&
                      onboarding?.appStoreData?.townCity &&
                      onboarding?.appStoreData?.stateProvince &&
                      onboarding?.appStoreData?.postalCode &&
                      onboarding?.appStoreData?.phoneNumber &&
                      onboarding?.appStoreData?.contactWorkPhone &&
                      onboarding?.appStoreData?.contactWorkEmail)))
              )
            ),
          },
        ]),
    {
      id: 'technical_sms',
      label: 'SMS Setup',
      parentId: 'technical',
      isComplete:
        !!platform.twilioPhoneNumber || !!platform.twilio10dlcExcluded,
    },
    // Customize Platform section
    {
      id: 'customize',
      label: 'Customize Platform',
      isComplete: false, // Will be calculated based on sub-items
    },
    {
      id: 'customize_phase1',
      label: 'Phase 1',
      parentId: 'customize',
      isComplete: !!(
        isCsmTaskComplete(onboarding.csmSetup?.schedulingCompleted) &&
        isCsmTaskComplete(onboarding.csmSetup?.servicesCompleted) &&
        isCsmTaskComplete(onboarding.csmSetup?.packagesCompleted)
      ),
    },
    {
      id: 'customize_phase2',
      label: 'Phase 2',
      parentId: 'customize',
      isComplete: !!(
        isCsmTaskComplete(onboarding.csmSetup?.programmingCompleted) &&
        isCsmTaskComplete(onboarding.csmSetup?.exercisesCompleted) &&
        isCsmTaskComplete(onboarding.csmSetup?.assessmentsCompleted)
      ),
    },
    {
      id: 'customize_phase3',
      label: 'Phase 3',
      parentId: 'customize',
      isComplete: !!(
        isCsmTaskComplete(onboarding.csmSetup?.automationsCompleted) &&
        isCsmTaskComplete(onboarding.csmSetup?.appAlertsCompleted) &&
        isCsmTaskComplete(onboarding.csmSetup?.customMeasurementsCompleted)
      ),
    },
    {
      id: 'customize_launch',
      label: 'Launch',
      parentId: 'customize',
      isComplete: !!(
        isCsmTaskComplete(onboarding.csmSetup?.appReviewCompleted) &&
        isCsmTaskComplete(onboarding.csmSetup?.launchCallCompleted)
      ),
    },
  ];

  // Update parent steps based on their sub-items
  steps.forEach((step) => {
    if (
      step.id === 'branding' ||
      step.id === 'technical' ||
      step.id === 'customize'
    ) {
      const subSteps = steps.filter((s) => s.parentId === step.id);
      step.isComplete = subSteps.every((subStep) => subStep.isComplete);
    }
  });

  // Get all steps that are leaf nodes (either children or parentless steps)
  const leafSteps = steps.filter((step) => {
    const hasChildren = steps.some((s) => s.parentId === step.id);
    return !hasChildren;
  });

  const completedSteps = leafSteps.filter((step) => step.isComplete).length;
  const totalSteps = leafSteps.length;
  const progressPercentage = Math.round((completedSteps / totalSteps) * 100);

  return {
    steps,
    completedSteps,
    totalSteps,
    progressPercentage,
  };
};
